import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Clock, Loader2, Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';

interface Appointment {
  id: string;
  name: string;
  service_type: string;
  appointment_type: string;
  preferred_date: string | null;
  preferred_time: string | null;
  status: string;
}

interface ModifyAppointmentDialogProps {
  appointment: Appointment;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function ModifyAppointmentDialog({
  appointment,
  open,
  onOpenChange,
}: ModifyAppointmentDialogProps) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [availableSlots, setAvailableSlots] = useState<string[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();
  const [selectedTime, setSelectedTime] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (open) {
      setSelectedDate(undefined);
      setSelectedTime('');
      setReason('');
    }
  }, [open]);

  useEffect(() => {
    if (selectedDate) {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate]);

  const fetchAvailableSlots = async (date: Date) => {
    setLoadingSlots(true);
    try {
      const dayOfWeek = date.getDay();
      const dateStr = date.toISOString().split('T')[0];

      const { data: availability } = await supabase
        .from('admin_availability')
        .select('*')
        .eq('day_of_week', dayOfWeek)
        .eq('is_available', true);

      if (!availability || availability.length === 0) {
        setAvailableSlots([]);
        return;
      }

      const { data: blockedSlots } = await supabase
        .from('blocked_time_slots')
        .select('*')
        .eq('blocked_date', dateStr);

      const { data: bookedAppts } = await supabase
        .from('appointments')
        .select('preferred_time')
        .eq('preferred_date', dateStr)
        .in('status', ['confirmed', 'pending'])
        .neq('id', appointment.id); // Exclude current appointment

      const slots: string[] = [];
      for (const avail of availability) {
        const [startHour, startMin] = avail.start_time.split(':').map(Number);
        const [endHour, endMin] = avail.end_time.split(':').map(Number);
        const slotDuration = avail.slot_duration_minutes || 60;

        let currentHour = startHour;
        let currentMin = startMin;

        while (
          currentHour < endHour ||
          (currentHour === endHour && currentMin < endMin)
        ) {
          const hour12 = currentHour % 12 || 12;
          const ampm = currentHour < 12 ? 'AM' : 'PM';
          const timeStr = `${hour12}:${currentMin.toString().padStart(2, '0')} ${ampm}`;

          const isBlocked = blockedSlots?.some((block) => {
            if (block.is_all_day) return true;
            return false;
          });

          const isBooked = bookedAppts?.some((appt) => appt.preferred_time === timeStr);

          if (!isBlocked && !isBooked) {
            slots.push(timeStr);
          }

          currentMin += slotDuration;
          if (currentMin >= 60) {
            currentHour += Math.floor(currentMin / 60);
            currentMin = currentMin % 60;
          }
        }
      }

      setAvailableSlots(slots);
    } catch (error) {
      console.error('Error fetching available slots:', error);
      setAvailableSlots([]);
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedDate || !selectedTime || !reason.trim()) {
      toast({
        title: 'Error',
        description: 'Please fill in all required fields',
        variant: 'destructive',
      });
      return;
    }

    try {
      setLoading(true);

      const { error } = await supabase
        .from('appointments')
        .update({
          modification_request: {
            requested_date: selectedDate.toISOString().split('T')[0],
            requested_time: selectedTime,
            reason: reason.trim(),
            requested_at: new Date().toISOString(),
          },
          modification_status: 'pending',
        })
        .eq('id', appointment.id);

      if (error) throw error;

      toast({
        title: 'Change Request Submitted',
        description: 'Your modification request has been sent to the admin for review',
      });

      onOpenChange(false);
    } catch (error: any) {
      console.error('Error submitting modification:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit modification request',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Request Appointment Change</DialogTitle>
          <DialogDescription>
            Request a change to your appointment. An admin will review and approve your request.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-3 p-4 bg-muted rounded-lg">
            <h4 className="font-semibold">Current Appointment</h4>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-muted-foreground">Service:</span>
                <p className="font-medium">{appointment.service_type}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Type:</span>
                <p className="font-medium">{appointment.appointment_type}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Date:</span>
                <p className="font-medium">
                  {appointment.preferred_date
                    ? format(new Date(appointment.preferred_date), 'MMM d, yyyy')
                    : 'Not set'}
                </p>
              </div>
              <div>
                <span className="text-muted-foreground">Time:</span>
                <p className="font-medium">{appointment.preferred_time || 'Not set'}</p>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>New Date *</Label>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={setSelectedDate}
            disabled={(date) => {
              const today = new Date();
              today.setHours(0, 0, 0, 0);
              return date < today;
            }}
              className="rounded-md border"
            />
          </div>

          {selectedDate && (
            <div className="space-y-2">
              <Label>New Time Slot *</Label>
              {loadingSlots ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : availableSlots.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No available slots for this date
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {availableSlots.map((slot) => (
                    <Button
                      key={slot}
                      type="button"
                      variant={selectedTime === slot ? 'default' : 'outline'}
                      className="h-auto py-2"
                      onClick={() => setSelectedTime(slot)}
                    >
                      <Clock className="h-4 w-4 mr-1" />
                      {slot}
                    </Button>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="reason">Reason for Change *</Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Please explain why you need to change your appointment..."
              required
            />
          </div>

          <div className="flex gap-3 justify-end">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || !selectedDate || !selectedTime || !reason.trim()}
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Submit Request
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
